'use client';

// The phone sheet's intro state, shared by `Modal` (owner), `SheetIntro`
// (writer) and `ModalHead` (reader), BL-2402. Its own module so neither
// component has to import the other.

import { createContext, useContext, useMemo, useState } from 'react';

/**
 * Where the sheet's on-screen title is: `none` (no intro, the header keeps its
 * title), `hidden` (the intro is in view, the header title is empty) or
 * `shown` (the intro scrolled away, the header shows the title).
 */
export type SheetIntroState = 'none' | 'hidden' | 'shown';

/** The intro state plus its setter. */
export interface SheetIntroHandle {
    state: SheetIntroState;
    setState: (next: SheetIntroState) => void;
}

/** The card's (and the outside's) value: no intro, nothing to hide. */
export const NO_SHEET_INTRO: SheetIntroHandle = { state: 'none', setState: () => {} };

/** `none` with a no-op setter outside a sheet, so the card never hides its title. */
export const SheetIntroContext = createContext<SheetIntroHandle>(NO_SHEET_INTRO);

/**
 * The value a sheet Modal provides. Called on every render (hooks before the
 * Modal's early returns), and only handed down when the Modal is a sheet.
 *
 * @returns A stable handle while the state is unchanged.
 */
export function useSheetIntroHandle(): SheetIntroHandle {
    const [state, setState] = useState<SheetIntroState>('none');
    return useMemo(() => ({ state, setState }), [state]);
}

/**
 * The intro state of the enclosing sheet.
 *
 * @returns The handle, or the no-intro default outside a sheet.
 */
export function useSheetIntro(): SheetIntroHandle {
    return useContext(SheetIntroContext);
}
